import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { CardImage } from './CardImage';
import { CardBugReportModal } from './member/CardBugReportModal';
import { useAuth } from './AuthProvider';
import { useNotification } from './member/NotificationProvider';
import { getBinders, addCardToList, addCardToBinder } from '../services/supabaseService';

export const parseCardSet = (setVal: any, cardFallback?: any): string => {
  if (!setVal && cardFallback) {
    return parseCardSet(
      cardFallback.set ||
      cardFallback.set_name ||
      cardFallback.setName ||
      cardFallback.variants?.[0]?.set ||
      cardFallback.variants?.[0]?.set_name ||
      cardFallback.variants?.[0]?.setName ||
      cardFallback.set_code ||
      cardFallback.setCode ||
      cardFallback.variants?.[0]?.set_code ||
      cardFallback.variants?.[0]?.setCode
    );
  }
  if (!setVal) return '';
  if (typeof setVal === 'string') return setVal;
  if (typeof setVal === 'object') {
    return (
      setVal.set_name ||
      setVal.name ||
      setVal.beauty_name ||
      setVal.title ||
      setVal.set_code ||
      setVal.prefix ||
      setVal.code ||
      setVal.setName ||
      (typeof setVal.set === 'object' ? parseCardSet(setVal.set) : (typeof setVal.set === 'string' ? setVal.set : '')) ||
      ''
    );
  }
  return String(setVal);
};

export const getTcgPlayerVariantImageUrl = (v: any): string | null => {
  if (!v) return null;
  const tcgId = v.tcgplayerId ?? v.tcgplayer_id ?? v.tcgPlayerId;
  if (tcgId !== undefined && tcgId !== null) {
    const cleanId = String(tcgId).trim();
    if (cleanId && cleanId !== '0' && cleanId !== 'null' && cleanId !== 'undefined') {
      return `https://tcgplayer-cdn.tcgplayer.com/product/${cleanId}_in_1000x1000.jpg`;
    }
  }
  return null;
};

export const parseCardImage = (card: any): string | undefined => {
  if (!card) return undefined;
  if (typeof card === 'string' && card.trim()) return card.trim();
  if (typeof card.imageUrl === 'string' && card.imageUrl) return card.imageUrl;
  if (typeof card.image_url === 'string' && card.image_url) return card.image_url;
  if (typeof card.image === 'string' && card.image) return card.image;
  
  const imgs = card.images || card.image || card.image_uris;
  if (imgs) {
    if (typeof imgs === 'string') return imgs;
    if (Array.isArray(imgs)) {
      const first = imgs[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object') {
        return first.large || first.medium || first.small || first.url || first.src || first.image || undefined;
      }
    }
    if (typeof imgs === 'object') {
      return imgs.large || imgs.image_url || imgs.imageUrl || imgs.medium || imgs.small || imgs.png || imgs.normal || imgs.url || imgs.src || undefined;
    }
  }

  // Check editions if card.images not present
  if (Array.isArray(card.editions) && card.editions.length > 0) {
    const firstEd = card.editions[0];
    if (firstEd?.image) return firstEd.image;
    if (firstEd?.imageUrl) return firstEd.imageUrl;
    if (firstEd?.image_url) return firstEd.image_url;
  }
  if (Array.isArray(card.result_editions) && card.result_editions.length > 0) {
    const firstEd = card.result_editions[0];
    if (firstEd?.image) return firstEd.image;
  }

  // Fallback for variants with tcgplayerId
  const tcgVariantImg = getTcgPlayerVariantImageUrl(card);
  if (tcgVariantImg) return tcgVariantImg;

  return undefined;
};

export const parseCardRarity = (rarityVal: any): string => {
  if (rarityVal === undefined || rarityVal === null || rarityVal === '') return '';
  if (typeof rarityVal === 'string') return rarityVal;
  if (typeof rarityVal === 'number') {
    const RARITY_MAP: Record<number, string> = {
      1: 'Common (C)',
      2: 'Uncommon (U)',
      3: 'Rare (R)',
      4: 'Super Rare (SR)',
      5: 'Ultra Rare (UR)',
      6: 'Promo / Special (PR)',
      7: 'Secret Rare (SEC)',
      8: 'Collector Rare (CR)',
      9: 'Collector Super Rare (CSR)',
    };
    return RARITY_MAP[rarityVal] || `Raridade ${rarityVal}`;
  }
  if (typeof rarityVal === 'object') {
    return rarityVal.name || rarityVal.label || rarityVal.code || parseCardSet(rarityVal) || '';
  }
  return String(rarityVal);
};

export const parseCardCost = (costVal: any, cardObj?: any): string | null => {
  if (costVal !== undefined && costVal !== null && costVal !== '') {
    if (typeof costVal === 'object') {
      if (costVal.value !== undefined && costVal.value !== null) {
        const typeLabel = costVal.type ? ` (${costVal.type})` : '';
        return `${costVal.value}${typeLabel}`;
      }
      if (costVal.cost !== undefined && costVal.cost !== null) return String(costVal.cost);
      if (costVal.amount !== undefined && costVal.amount !== null) return String(costVal.amount);
      return null;
    }
    return String(costVal);
  }
  if (cardObj) {
    if (cardObj.cost_memory !== undefined && cardObj.cost_memory !== null) {
      return `${cardObj.cost_memory} (Memory)`;
    }
    if (cardObj.cost_reserve !== undefined && cardObj.cost_reserve !== null) {
      return `${cardObj.cost_reserve} (Reserve)`;
    }
  }
  return null;
};

export const parseCardText = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'string') {
    return val.replace(/<br\s*\/?>/gi, '\n').trim();
  }
  if (Array.isArray(val)) {
    return val.map(parseCardText).filter(Boolean).join('\n\n');
  }
  if (typeof val === 'object') {
    const parts: string[] = [];
    if (val.card) {
      const c = parseCardText(val.card);
      if (c) parts.push(c);
    }
    if (val.rule) {
      const r = parseCardText(val.rule);
      if (r) parts.push(r);
    }
    if (val.ability) {
      const a = parseCardText(val.ability);
      if (a) parts.push(a);
    }
    if (val.effect) {
      const e = parseCardText(val.effect);
      if (e) parts.push(e);
    }
    if (val.effect_raw) {
      const er = parseCardText(val.effect_raw);
      if (er && !parts.includes(er)) parts.push(er);
    }

    const sortedKeys = Object.keys(val).sort();
    sortedKeys.forEach(k => {
      if (
        (k.startsWith('attack') || k.startsWith('ability') || k.startsWith('effect') || k.startsWith('rule') || k.startsWith('desc')) &&
        k !== 'card' && k !== 'ability' && k !== 'effect' && k !== 'effect_raw' && k !== 'rule'
      ) {
        const item = val[k];
        if (typeof item === 'string' && item.trim()) {
          parts.push(item.replace(/<br\s*\/?>/gi, '\n').trim());
        } else if (item && typeof item === 'object') {
          const sub = parseCardText(item);
          if (sub) parts.push(sub);
        }
      }
    });

    if (parts.length === 0) {
      Object.values(val).forEach(v => {
        if (typeof v === 'string' && v.trim()) {
          parts.push(v.replace(/<br\s*\/?>/gi, '\n').trim());
        } else if (v && typeof v === 'object') {
          const sub = parseCardText(v);
          if (sub) parts.push(sub);
        }
      });
    }

    return parts.filter(Boolean).join('\n\n');
  }
  return String(val).trim();
};

const DetailField: React.FC<{ 
  label: string; 
  value: any; 
  highlight?: string; 
  isCircle?: boolean;
  icon?: string;
}> = ({ label, value, highlight = 'text-white', isCircle, icon }) => {
  let displayVal = '';
  if (value === null || value === undefined) {
    return null;
  } else if (typeof value === 'object') {
    if (Array.isArray(value)) {
      displayVal = value.map(v => typeof v === 'object' ? (v.name || v.label || JSON.stringify(v)) : String(v)).join(', ');
    } else {
      displayVal = value.name || value.beauty_name || value.title || value.value || value.set_code || value.code || parseCardSet(value) || parseCardText(value) || '';
    }
  } else {
    displayVal = String(value).trim();
  }

  if (!displayVal || displayVal === '[object Object]') return null;

  const showCircle = isCircle && displayVal.length <= 3;

  return (
    <div className="space-y-0.5">
      <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
      <div className="bg-slate-950/40 border border-white/5 rounded-lg px-2.5 py-1.5 flex items-center justify-between group hover:border-purple-500/30 transition-colors">
        <span className={`text-[11px] font-bold ${highlight} truncate`} title={displayVal}>{displayVal}</span>
        {showCircle ? (
          <span className="w-5 h-5 bg-emerald-500 text-slate-950 text-[9px] font-black rounded-full flex items-center justify-center shrink-0 ml-2">
            {displayVal}
          </span>
        ) : icon ? (
          <i className={`${icon} text-slate-500 text-[10px] ml-2`}></i>
        ) : null}
      </div>
    </div>
  );
};

const getTargetCard = (item: any) => (item?.card ? item.card : item);

export interface CardDetailModalProps {
  card: any | null;
  onClose: () => void;
  cardsList?: any[];
  onSelectCard?: (card: any) => void;
  gameName?: string;
  primaryAction?: {
    label: string;
    icon?: string;
    onClick?: (card: any) => void;
    className?: string;
    options?: {
      id?: string;
      label: string;
      icon?: string;
      color?: string;
      badge?: string;
      onClick: (card: any) => void;
    }[];
  };
  extraActions?: (card: any) => React.ReactNode;
  deckInfo?: {
    section?: string;
    quantity?: number;
  };
  wishlistAction?: {
    isAdded?: boolean;
    loading?: boolean;
    onClick: (card: any) => void;
  };
  onCardAdded?: (folderId: string, card: any) => void;
}

export const CardDetailModal: React.FC<CardDetailModalProps> = ({
  card,
  onClose,
  cardsList = [],
  onSelectCard,
  gameName,
  primaryAction,
  extraActions,
  deckInfo,
  wishlistAction,
  onCardAdded,
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [userBinders, setUserBinders] = useState<any[]>([]);
  const [addingToFolderId, setAddingToFolderId] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const [fullscreenImageCard, setFullscreenImageCard] = useState<any | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isActionOptionsOpen, setIsActionOptionsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user?.id) {
      const unsub = getBinders(user.id, (binders: any[]) => {
        if (Array.isArray(binders)) {
          setUserBinders(binders);
        }
      });
      return () => {
        if (typeof unsub === 'function') unsub();
      };
    }
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setIsActionOptionsOpen(false);
      }
    };
    if (isActionOptionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionOptionsOpen]);

  useEffect(() => {
    setSelectedVariant(null);
    setIsActionOptionsOpen(false);
  }, [card?.id, card?.code, card?.name]);

  if (!card) return null;

  const cardId = String(
    (card.external_id && typeof card.external_id === 'string' && card.external_id.trim())
      ? card.external_id.trim()
      : (card.id !== undefined && card.id !== null ? String(card.id).trim() : '')
  );
  const baseCardCode = String(
    card.code ||
    card.set_code ||
    card.number ||
    card.collector_number ||
    card.pokemon?.number ||
    ''
  ).trim();
  
  let cardType = '';
  if (typeof card.type === 'string' && card.type) {
    cardType = card.type;
  } else if (Array.isArray(card.types) && card.types.length > 0) {
    cardType = card.types.join(' / ');
  } else if (typeof card.cardType === 'string' && card.cardType) {
    cardType = card.cardType;
  } else if (card.supertype) {
    cardType = String(card.supertype);
  } else if (card.pokemon?.card_type) {
    cardType = String(card.pokemon.card_type);
  } else if (card.frameType) {
    cardType = String(card.frameType);
  } else if (card.product_type) {
    cardType = String(card.product_type);
  }

  const cardCostDisplay = parseCardCost(card.cost ?? card.playCost ?? card.play_cost ?? card.energyCost ?? card.energy_cost ?? card.cmc ?? card.manaCost ?? card.level ?? card.pitch, card);
  const cardRarityDisplay = parseCardRarity(card.rarity || card.set_rarity);
  
  const variantsList: any[] = (Array.isArray(card.variants) && card.variants.length > 0)
    ? card.variants
    : (Array.isArray(card.editions) && card.editions.length > 0)
      ? card.editions
      : (Array.isArray(card.result_editions) && card.result_editions.length > 0)
        ? card.result_editions
        : [];

  const parseVariantData = (v: any) => {
    if (!v) return null;
    const variantCode = v.variant_code || v.variantCode || null;
    const variantExternalId = v.variant_external_id || v.variantExternalId || null;
    const rawSetCode = v.set_code || v.setCode || (typeof v.set === 'object' ? (v.set?.set_code || v.set?.code) : null) || (variantCode && variantCode.includes('-') ? variantCode.split('-')[0] : null) || v.code || v.number || null;
    const setName = v.set_name || v.setName || (typeof v.set === 'string' ? v.set : v.set?.set_name || v.set?.name || v.set?.title) || null;
    const setCode = rawSetCode || variantCode || v.card_number || (v.set?.prefix && v.collector_number ? `${v.set.prefix}-${v.collector_number}` : (v.set?.prefix || v.collector_number)) || null;
    const rarity = parseCardRarity(v.set_rarity || v.rarity || v.setRarity || v.card_rarity || v.rarity_code);
    const printing = v.printing || v.finish || v.edition || v.variant || v.subTypeName || (v.circulations?.[0]?.kind ? `${v.circulations[0].kind}${v.circulations[0].foil ? ' FOIL' : ''}` : null) || v.circulationTemplates?.[0]?.name || null;
    const condition = v.condition || v.itemCondition || null;
    const language = v.language || v.lang || v.set?.language || null;
    const tcgPlayerId = v.tcgplayerId || v.tcgplayer_id || null;
    const tcgPlayerImageUrl = getTcgPlayerVariantImageUrl(v);
    const illustrator = v.illustrator || v.artist || null;

    let price = v.price;
    if (price === undefined || price === null || isNaN(Number(price)) || Number(price) === 0) {
      const rawVal = v.marketPrice ?? v.set_price ?? v.midPrice ?? v.avgPrice ?? v.lowPrice;
      if (rawVal !== undefined && rawVal !== null && !isNaN(Number(rawVal))) {
        price = Math.round(Number(rawVal) * 5 * 100) / 100;
      }
    }
    const priceNum = (price !== undefined && price !== null && !isNaN(Number(price))) ? Math.round(Number(price) * 100) / 100 : 0;
    
    const lowPrice = v.lowPrice !== undefined && v.lowPrice !== null ? Math.round(Number(v.lowPrice) * 5 * 100) / 100 : null;
    const marketPrice = (v.marketPrice ?? v.set_price) !== undefined && (v.marketPrice ?? v.set_price) !== null ? Math.round(Number(v.marketPrice ?? v.set_price) * 5 * 100) / 100 : null;
    const highPrice = v.highPrice !== undefined && v.highPrice !== null ? Math.round(Number(v.highPrice) * 5 * 100) / 100 : null;
    const midPrice = v.midPrice !== undefined && v.midPrice !== null ? Math.round(Number(v.midPrice) * 5 * 100) / 100 : null;

    let primaryLabel = variantCode || setCode || v.circulationTemplates?.[0]?.name || printing || rarity || v.name || v.justname || v.juSTname || 'Edição / Variante';

    return {
      rawSetCode,
      setCode,
      variantCode,
      variantExternalId,
      rarity,
      printing,
      condition,
      language,
      setName,
      tcgPlayerId,
      tcgPlayerImageUrl,
      illustrator,
      priceNum,
      lowPrice,
      marketPrice,
      highPrice,
      midPrice,
      primaryLabel,
      raw: v
    };
  };

  const getVariantPriceNum = (v: any): number => {
    const data = parseVariantData(v);
    return data?.priceNum || 0;
  };

  let cardPrice: string | null = null;
  let resolvedCardPriceNum: number = (typeof card.price === 'number' && card.price > 0) ? card.price : 0;
  if (!resolvedCardPriceNum && variantsList && variantsList.length > 0) {
    const validVariantPrices = variantsList
      .map((v: any) => getVariantPriceNum(v))
      .filter((p: number) => p > 0);
    if (validVariantPrices.length > 0) {
      resolvedCardPriceNum = Math.min(...validVariantPrices);
    }
  }

  if (resolvedCardPriceNum > 0) {
    cardPrice = `R$ ${resolvedCardPriceNum.toFixed(2)}`;
  }

  const selectedVariantInfo = selectedVariant ? parseVariantData(selectedVariant) : null;
  const cardCode = String(selectedVariantInfo?.setCode || baseCardCode).trim();
  const selectedVariantPriceNum = selectedVariantInfo?.priceNum || 0;
  let activePriceStr: string | null = null;
  if (selectedVariant && selectedVariantPriceNum > 0) {
    activePriceStr = `R$ ${selectedVariantPriceNum.toFixed(2)}`;
  } else if (cardPrice) {
    activePriceStr = cardPrice;
  }

  const cardEffect = parseCardText(card.effect || card.effect_raw || card.description || card.oracle_text || card.desc || card.ability || card.frontText || card.text);
  const cardFlavor = card.flavor || selectedVariant?.flavor || selectedVariantInfo?.raw?.flavor;
  const cardSet = parseCardSet(card.set, card);
  const defaultCardImage = parseCardImage(card);
  const selectedVariantTcgImage = getTcgPlayerVariantImageUrl(selectedVariant);
  const activeImage = selectedVariant 
    ? (selectedVariantTcgImage || parseCardImage(selectedVariant) || selectedVariant.imageUrl || selectedVariant.image_url || selectedVariant.image || defaultCardImage)
    : defaultCardImage;

  // Collect card tags
  const cardTags: string[] = [];
  if (card.domain) cardTags.push(String(card.domain));
  if (card.attribute) cardTags.push(String(card.attribute));
  if (card.element) cardTags.push(String(card.element));
  if (Array.isArray(card.elements)) card.elements.forEach((e: any) => cardTags.push(String(e)));
  if (card.family) cardTags.push(String(card.family));
  if (card.race) cardTags.push(String(card.race));
  if (card.trait) cardTags.push(String(card.trait));
  if (Array.isArray(card.traits)) card.traits.forEach((t: any) => cardTags.push(String(t)));
  if (Array.isArray(card.classes)) card.classes.forEach((c: any) => cardTags.push(String(c)));
  if (Array.isArray(card.subtypes)) card.subtypes.forEach((s: any) => cardTags.push(String(s)));
  if (Array.isArray(card.types)) card.types.forEach((t: any) => cardTags.push(String(t)));
  if (card.category) cardTags.push(String(card.category));
  if (card.pokemonType) cardTags.push(String(card.pokemonType));
  if (card.stage) cardTags.push(String(card.stage));
  if (card.pokemon?.card_type) cardTags.push(String(card.pokemon.card_type));
  if (card.pokemon?.stage) cardTags.push(String(card.pokemon.stage));
  if (card.form) cardTags.push(String(card.form));
  const uniqueTags = Array.from(new Set(cardTags.filter(Boolean)));

  // Attributes grid
  const fields: { label: string; value: any; highlight?: string; isCircle?: boolean }[] = [];
  
  // ID da Carta (propriedade distinta)
  if (cardId) {
    fields.push({ label: 'ID da Carta', value: cardId, highlight: 'text-purple-300 font-mono font-bold' });
  }

  // Código da Carta (propriedade distinta)
  if (cardCode) {
    fields.push({ label: 'Código', value: cardCode, highlight: 'text-blue-300 font-mono font-bold' });
  }

  if (selectedVariantInfo?.setName) {
    fields.push({ label: 'Coleção', value: selectedVariantInfo.setName });
  } else if (cardSet) {
    fields.push({ label: 'Coleção', value: cardSet });
  }

  if (card.game || gameName) fields.push({ label: 'Jogo', value: card.game || gameName });

  if (selectedVariantInfo?.rarity) {
    fields.push({ label: 'Raridade', value: selectedVariantInfo.rarity, highlight: 'text-orange-400 font-bold' });
  } else if (cardRarityDisplay) {
    fields.push({ label: 'Raridade', value: cardRarityDisplay, highlight: 'text-orange-400' });
  }

  if (activePriceStr) fields.push({ label: selectedVariant ? 'Preço da Variante' : 'Preço Estimado', value: activePriceStr, highlight: 'text-emerald-400 font-black' });
  
  if (selectedVariantInfo) {
    if (selectedVariantInfo.condition) {
      fields.push({ label: 'Condição', value: selectedVariantInfo.condition, highlight: 'text-emerald-300 font-semibold' });
    }
    if (selectedVariantInfo.language) {
      fields.push({ label: 'Idioma', value: selectedVariantInfo.language, highlight: 'text-slate-200' });
    }
    if (selectedVariantInfo.printing && selectedVariantInfo.printing !== selectedVariantInfo.rarity) {
      fields.push({ label: 'Acabamento', value: selectedVariantInfo.printing, highlight: 'text-purple-300 font-semibold' });
    }
    if (selectedVariantInfo.tcgPlayerId) {
      fields.push({ label: 'TCGPlayer ID', value: `#${selectedVariantInfo.tcgPlayerId}`, highlight: 'text-blue-400 font-mono' });
    }
    if (selectedVariantInfo.lowPrice && selectedVariantInfo.highPrice) {
      fields.push({ 
        label: 'Faixa (Mín - Máx)', 
        value: `R$ ${selectedVariantInfo.lowPrice.toFixed(2)} - R$ ${selectedVariantInfo.highPrice.toFixed(2)}`,
        highlight: 'text-slate-300 text-xs'
      });
    }
  }

  if (cardType) fields.push({ label: 'Tipo', value: cardType });

  if (Array.isArray(card.classes) && card.classes.length > 0) {
    fields.push({ label: 'Classe(s)', value: card.classes.join(', ') });
  }

  if (Array.isArray(card.subtypes) && card.subtypes.length > 0) {
    fields.push({ label: 'Subtipos', value: card.subtypes.join(', ') });
  }
  
  const stageVal = card.pokemon?.stage || card.stage;
  if (stageVal) fields.push({ label: 'Estágio', value: String(stageVal), highlight: 'text-purple-300' });

  if (card.level !== undefined && card.level !== null && card.level !== '') {
    fields.push({ label: 'Nível', value: String(card.level), highlight: 'text-amber-400' });
  }

  if (cardCostDisplay) {
    fields.push({ label: 'Custo', value: cardCostDisplay, isCircle: cardCostDisplay.length <= 2 });
  }

  if (card.cost_memory !== undefined && card.cost_memory !== null) {
    fields.push({ label: 'Custo de Memória', value: String(card.cost_memory), highlight: 'text-cyan-400' });
  }
  if (card.cost_reserve !== undefined && card.cost_reserve !== null) {
    fields.push({ label: 'Custo de Reserva', value: String(card.cost_reserve), highlight: 'text-indigo-400' });
  }

  const colorVal = card.color || (Array.isArray(card.colors) ? card.colors.join(', ') : card.colors) || card.attribute || card.element || (Array.isArray(card.elements) ? card.elements.join(', ') : null) || card.domain || card.pokemon?.card_type || card.pokemonType;
  if (colorVal) fields.push({ label: 'Cor / Elemento', value: String(colorVal) });

  const powerVal = card.power || card.atk || card.bp || card.dp || card.might;
  if (powerVal !== undefined && powerVal !== null && powerVal !== '') {
    fields.push({ label: 'Poder / Ataque', value: String(powerVal), highlight: 'text-purple-400' });
  }

  const defenseVal = card.pokemon?.hp ?? card.hp ?? card.defense ?? card.defence ?? card.guardian?.defence ?? card.def ?? card.counter ?? card.health;
  if (defenseVal !== undefined && defenseVal !== null && defenseVal !== '') {
    fields.push({ label: 'Defesa / HP', value: String(defenseVal), highlight: 'text-blue-400' });
  }

  const lifeVal = card.life ?? card.guardian?.life;
  if (lifeVal !== undefined && lifeVal !== null && lifeVal !== '') {
    fields.push({ label: 'Vida', value: String(lifeVal), highlight: 'text-red-400' });
  }

  if (card.durability !== undefined && card.durability !== null && card.durability !== '') {
    fields.push({ label: 'Durabilidade', value: String(card.durability), highlight: 'text-emerald-400' });
  }

  if (card.speed !== undefined && card.speed !== null && card.speed !== '') {
    fields.push({ label: 'Velocidade', value: String(card.speed), highlight: 'text-yellow-400' });
  }

  const weaknessVal = card.weaknesses || card.weakness || card.pokemon?.weakness;
  if (weaknessVal) fields.push({ label: 'Fraqueza', value: String(weaknessVal), highlight: 'text-red-400' });

  const resistanceVal = card.resistances || card.resistance || card.pokemon?.resistance;
  if (resistanceVal && resistanceVal !== 'None' && resistanceVal !== 'none') {
    fields.push({ label: 'Resistência', value: String(resistanceVal), highlight: 'text-emerald-400' });
  }

  const retreatCostVal = card.retreat ?? card.retreat_cost ?? card.retreatCost ?? card.pokemon?.retreat_cost ?? card.pokemon?.retreatCost;
  if (retreatCostVal !== undefined && retreatCostVal !== null && retreatCostVal !== '') {
    fields.push({ label: 'Custo Recuo', value: String(retreatCostVal) });
  }

  const traitVal = card.family || card.race || card.trait || (Array.isArray(card.traits) ? card.traits.join(', ') : null);
  if (traitVal) fields.push({ label: 'Traço / Família', value: String(traitVal) });

  const artistVal = card.illustrator || card.pokemon?.artist || card.artist || selectedVariantInfo?.illustrator;
  if (artistVal) fields.push({ label: 'Ilustrador / Artista', value: String(artistVal) });

  const sectionName = deckInfo?.section || card.section;
  const quantityVal = deckInfo?.quantity ?? card.quantity;
  if (sectionName) fields.push({ label: 'Seção no Deck', value: sectionName, highlight: 'text-purple-300' });
  if (quantityVal) fields.push({ label: 'Quantidade no Deck', value: `${quantityVal}x`, highlight: 'text-white' });

  const activeTcgPlayerId = selectedVariant?.tcgplayerId || selectedVariant?.tcgplayer_id || card?.variants?.[0]?.tcgplayerId || card?.set?.tcgplayer_id || card?.tcgplayer_id || card?.tcgplayerId;
  const tcgPlayerUrl = activeTcgPlayerId 
    ? `https://www.tcgplayer.com/product/${activeTcgPlayerId}`
    : `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(card.name)}`;

  const currentCardIndex = cardsList.length > 0
    ? cardsList.findIndex((c: any) => {
        const target = getTargetCard(c);
        if (target.id && card.id && String(target.id) === String(card.id)) return true;
        if (target.external_id && card.external_id && String(target.external_id) === String(card.external_id)) return true;
        if (target.card_id && card.card_id && String(target.card_id) === String(card.card_id)) return true;
        if (target.dbId && card.dbId && String(target.dbId) === String(card.dbId)) return true;
        return false;
      })
    : -1;

  const selectedVariantIndex = selectedVariant && variantsList.length > 0
    ? variantsList.indexOf(selectedVariant)
    : -1;
  const variantNumber = selectedVariant
    ? (selectedVariant.variant_number ?? selectedVariant.variantNumber ?? (selectedVariantIndex >= 0 ? selectedVariantIndex + 1 : 1))
    : (card.variant_number && Number(card.variant_number) > 0 ? Number(card.variant_number) : (card.variantNumber && Number(card.variantNumber) > 0 ? Number(card.variantNumber) : 1));

  const effectiveCardId = (card.id !== undefined && card.id !== null ? String(card.id).trim() : '') || 
    (card.external_id && typeof card.external_id === 'string' ? card.external_id.trim() : '');

  const firstVariant = Array.isArray(card.variants) && card.variants.length > 0 ? card.variants[0] : null;
  const baseSetCode = card.set_code || card.setCode || 
    (typeof card.set === 'object' ? (card.set?.set_code || card.set?.code) : null) || 
    firstVariant?.set_code || 
    (typeof firstVariant?.set === 'object' ? (firstVariant.set?.set_code || firstVariant.set?.code) : null) || 
    (firstVariant?.variant_code && firstVariant.variant_code.includes('-') ? firstVariant.variant_code.split('-')[0] : null) || 
    null;
  const baseRarity = card.rarity || card.set_rarity || 
    firstVariant?.rarity || firstVariant?.set_rarity || firstVariant?.rarity_code || 
    card.md_rarity || 
    null;

  const cardPayloadWithVariant = selectedVariant
    ? {
        ...card,
        id: effectiveCardId || selectedVariant.id?.toString(),
        external_id: effectiveCardId || selectedVariant.id?.toString() || card.code,
        variant: selectedVariant,
        variant_number: variantNumber,
        variant_name: selectedVariant.variant || selectedVariant.name || selectedVariant.printing || selectedVariant.rarity,
        price: selectedVariantPriceNum > 0 ? selectedVariantPriceNum : card.price,
        imageUrl: activeImage || card.imageUrl,
        image_url: activeImage || card.image_url,
        set_code: selectedVariantInfo?.rawSetCode || selectedVariantInfo?.setCode || selectedVariant.set_code || (typeof selectedVariant.set === 'object' ? selectedVariant.set?.set_code : null) || (selectedVariant.variant_code && selectedVariant.variant_code.includes('-') ? selectedVariant.variant_code.split('-')[0] : null) || selectedVariant.code || baseSetCode || null,
        rarity: selectedVariantInfo?.rarity || selectedVariant.rarity || selectedVariant.set_rarity || selectedVariant.rarity_code || baseRarity || null,
        variant_code: selectedVariant.variant_code || selectedVariant.variantCode || card.variant_code || card.variantCode || null,
        variant_external_id: selectedVariant.variant_external_id || selectedVariant.variantExternalId || card.variant_external_id || card.variantExternalId || null,
        tcgplayer_id: selectedVariant.tcgplayerId || selectedVariant.tcgplayer_id || card.tcgplayer_id || null,
        tcgplayerId: selectedVariant.tcgplayerId || selectedVariant.tcgplayer_id || card.tcgplayerId || null,
      }
    : {
        ...card,
        id: effectiveCardId,
        external_id: effectiveCardId || card.code,
        variant_number: variantNumber,
        set_code: baseSetCode,
        rarity: baseRarity,
        variant_code: card.variant_code || card.variantCode || firstVariant?.variant_code || null,
        variant_external_id: card.variant_external_id || card.variantExternalId || firstVariant?.variant_external_id || null,
        tcgplayer_id: card.tcgplayer_id || card.tcgplayerId || firstVariant?.tcgplayerId || firstVariant?.tcgplayer_id || null,
        tcgplayerId: card.tcgplayer_id || card.tcgplayerId || firstVariant?.tcgplayerId || firstVariant?.tcgplayer_id || null,
      };

  const defaultSystemFolders = [
    { id: 'colecao', name: 'Coleção', icon: 'fas fa-box-archive', color: 'bg-purple-500', badge: 'Principal' },
    { id: 'wishlist', name: 'Wishlist', icon: 'fas fa-heart', color: 'bg-pink-500', badge: 'Wishlist' },
    { id: 'offerlist', name: 'Offerlist', icon: 'fas fa-arrow-right-arrow-left', color: 'bg-emerald-500', badge: 'Trocas' },
  ];

  const handleDefaultAddToFolder = async (folderId: string, folderName: string, isBinder: boolean = false) => {
    if (!user) {
      showNotification('Faça login para salvar cartas em suas pastas.', 'info');
      return;
    }
    setAddingToFolderId(folderId);
    try {
      if (isBinder) {
        await addCardToBinder(user.id, folderId, cardPayloadWithVariant);
      } else {
        const listName = folderId === 'colecao' ? 'cards' : folderId as any;
        await addCardToList(user.id, listName, cardPayloadWithVariant, 1);
      }
      showNotification(`"${card.name}" adicionada em "${folderName}"!`, 'success');
      if (onCardAdded) {
        onCardAdded(folderId, cardPayloadWithVariant);
      }
      setIsActionOptionsOpen(false);
    } catch (err: any) {
      showNotification(err.message || 'Erro ao adicionar carta', 'error');
    } finally {
      setAddingToFolderId(null);
    }
  };

  const effectivePrimaryAction: CardDetailModalProps['primaryAction'] = primaryAction || (user ? {
    label: addingToFolderId ? 'Adicionando...' : 'Adicionar à Coleção ou Pastas',
    icon: addingToFolderId ? 'fas fa-spinner fa-spin' : 'fa-solid fa-folder-plus',
    className: 'w-full bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-[10px] tracking-widest py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer',
    options: [
      ...defaultSystemFolders.map(folder => ({
        id: folder.id,
        label: folder.name,
        icon: folder.icon,
        color: folder.color,
        badge: folder.badge,
        onClick: () => handleDefaultAddToFolder(folder.id, folder.name, false)
      })),
      ...userBinders.map(binder => ({
        id: String(binder.id),
        label: binder.name,
        icon: binder.icon ? (binder.icon.includes(' ') ? binder.icon : `fas ${binder.icon}`) : 'fas fa-folder',
        color: 'bg-purple-400',
        badge: 'Pasta',
        onClick: () => handleDefaultAddToFolder(String(binder.id), binder.name, true)
      }))
    ]
  } : {
    label: 'Adicionar à Coleção ou Pastas',
    icon: 'fa-solid fa-folder-plus',
    className: 'w-full bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-[10px] tracking-widest py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer',
    onClick: () => showNotification('Faça login para salvar cartas em suas pastas.', 'info')
  });

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-white/5 bg-slate-900/50">
              <div className="flex items-center space-x-2 min-w-0">
                <span className="text-xs font-black text-purple-400 uppercase tracking-widest">{card.game || gameName || 'TCG'}</span>
                <span className="text-slate-600">•</span>
                <h3 className="text-sm font-black text-white uppercase tracking-tight truncate">{card.name}</h3>
              </div>
              <div className="flex items-center space-x-1.5">
                <button 
                  type="button"
                  onClick={() => setIsReportModalOpen(true)}
                  title="Reportar erro nesta carta"
                  className="px-2.5 py-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 rounded-lg flex items-center space-x-1.5 transition-all text-xs font-bold cursor-pointer"
                >
                  <i className="fas fa-flag text-[10px] text-red-400"></i>
                  <span className="hidden sm:inline">Reportar Erro</span>
                </button>
                <button 
                  onClick={onClose}
                  className="w-8 h-8 hover:bg-slate-800 text-slate-400 hover:text-white rounded-full flex items-center justify-center transition-all shrink-0 ml-1 cursor-pointer"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row overflow-y-auto scrollbar-hide">
              {/* Left Section: Image & Actions */}
              <div className="lg:w-[350px] xl:w-[380px] p-6 sm:p-7 bg-slate-950 flex flex-col items-center justify-start space-y-6 border-r border-slate-800 shrink-0">
                <div 
                  onClick={() => setFullscreenImageCard(selectedVariant ? { ...card, ...selectedVariant, imageUrl: activeImage } : card)} 
                  className="relative group w-full max-w-[280px] sm:max-w-[320px] aspect-[63/88] rounded-2xl overflow-hidden cursor-pointer bg-slate-950 flex items-center justify-center border border-slate-800 hover:border-purple-500/50 transition-colors"
                  title="Clique para ver imagem em tela cheia"
                >
                  <CardImage 
                    src={activeImage || defaultCardImage} 
                    alt={card.name}
                    game={card.game || gameName}
                    gameId={card.game_id}
                    cardBackUrl={card.cardBackUrl || card.card_back_url}
                    objectFit="contain"
                    className="w-full h-full rounded-2xl" 
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex flex-col items-center justify-center text-white space-y-1.5">
                    <div className="w-10 h-10 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <i className="fas fa-expand text-sm text-purple-300"></i>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-200">Ver em Tela Cheia</span>
                  </div>
                </div>

                <div className="w-full space-y-3">
                  {effectivePrimaryAction && (
                    effectivePrimaryAction.options && effectivePrimaryAction.options.length > 0 ? (
                      <div className="w-full relative" ref={actionMenuRef}>
                        <button 
                          type="button"
                          onClick={() => setIsActionOptionsOpen(prev => !prev)}
                          disabled={Boolean(addingToFolderId)}
                          className={effectivePrimaryAction.className || "w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer"}
                        >
                          {effectivePrimaryAction.icon && <i className={effectivePrimaryAction.icon}></i>}
                          <span>{effectivePrimaryAction.label}</span>
                          <i className={`fas fa-chevron-down text-[9px] ml-1 transition-transform ${isActionOptionsOpen ? 'rotate-180' : ''}`}></i>
                        </button>

                        {isActionOptionsOpen && (
                          <div className="mt-2 bg-slate-900 border border-slate-800 rounded-xl p-2 shadow-xl space-y-1 z-30 max-h-60 overflow-y-auto">
                            {effectivePrimaryAction.options.map((opt) => (
                              <button
                                key={opt.id || opt.label}
                                type="button"
                                disabled={Boolean(addingToFolderId)}
                                onClick={() => {
                                  opt.onClick(cardPayloadWithVariant);
                                }}
                                className="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-slate-800 border border-transparent transition-colors flex items-center justify-between text-slate-200 group cursor-pointer disabled:opacity-40"
                              >
                                <div className="flex items-center space-x-2.5 min-w-0">
                                  {opt.icon ? (
                                    <i className={`${opt.icon.includes(' ') ? opt.icon : `fas ${opt.icon}`} text-purple-400 text-xs shrink-0 w-4 text-center`}></i>
                                  ) : opt.color ? (
                                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.color}`}></span>
                                  ) : (
                                    <i className="fas fa-folder text-purple-400 text-xs shrink-0 w-4 text-center"></i>
                                  )}
                                  <span className="font-semibold truncate">{opt.label}</span>
                                </div>
                                {opt.badge && (
                                  <span className="text-[9px] font-bold text-purple-300 bg-purple-500/20 px-1.5 py-0.5 rounded ml-2 shrink-0">
                                    {opt.badge}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => effectivePrimaryAction.onClick && effectivePrimaryAction.onClick(cardPayloadWithVariant)}
                        className={effectivePrimaryAction.className || "w-full bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-[10px] tracking-widest py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer"}
                      >
                        {effectivePrimaryAction.icon && <i className={effectivePrimaryAction.icon}></i>}
                        <span>{effectivePrimaryAction.label}</span>
                      </button>
                    )
                  )}
                  {extraActions && extraActions(cardPayloadWithVariant)}
                  <a
                    href={tcgPlayerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-[10px] tracking-widest py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <i className="fas fa-external-link-alt"></i>
                    <span>Ver no TCGPlayer</span>
                  </a>
                </div>
              </div>

              {/* Right Section: Detailed Stats */}
              <div className="flex-1 p-6 lg:p-8 space-y-6 bg-slate-900/30">
                
                {/* Top Stats Banner */}
                <div className="flex items-center justify-between pb-4 border-b border-white/5">
                  <div className="space-y-1">
                     <h2 className="text-xl font-black text-white uppercase tracking-tighter">
                       {cardCode ? `${cardCode} - ` : ''}{card.name}
                     </h2>
                  </div>
                </div>

                {/* Sections */}
                <div className="space-y-6">
                  {/* Effect Section */}
                  {(cardEffect || cardFlavor || (Array.isArray(card.rule) && card.rule.length > 0)) ? (
                    <div className="space-y-2">
                      <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Efeito / Descrição</h4>
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5 space-y-2">
                        {cardEffect && (
                          <p className="text-[11px] text-slate-300 leading-relaxed font-medium whitespace-pre-line">
                            {cardEffect}
                          </p>
                        )}
                        {cardFlavor && (
                          <p className="text-[10px] text-slate-400 font-serif italic pt-2 border-t border-white/5">
                            "{cardFlavor}"
                          </p>
                        )}
                        {Array.isArray(card.rule) && card.rule.length > 0 && (
                          <div className="space-y-1 pt-2 border-t border-white/5">
                            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Regras / Rulings</p>
                            <ul className="list-disc list-inside text-[10px] text-slate-400 space-y-0.5">
                              {card.rule.map((r: any, idx: number) => (
                                <li key={idx}>{typeof r === 'object' ? (r.rule || r.text || JSON.stringify(r)) : String(r)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Stats Grid */}
                  {fields.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Atributos</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {fields.map((f, i) => (
                          <DetailField key={i} label={f.label} value={f.value} highlight={f.highlight} isCircle={f.isCircle} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Variants Section */}
                  {variantsList.length > 0 && (
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center space-x-1.5">
                          <i className="fas fa-layer-group text-purple-400"></i>
                          <span>Variantes & Impressões ({variantsList.length})</span>
                        </h4>
                        {selectedVariant && (
                          <button
                            type="button"
                            onClick={() => setSelectedVariant(null)}
                            className="text-[9px] font-bold text-purple-400 hover:text-purple-300 underline uppercase tracking-wider"
                          >
                            Restaurar Padrão
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-800">
                        {variantsList.map((v: any, idx: number) => {
                          const vInfo = parseVariantData(v);
                          if (!vInfo) return null;
                          const vImg = vInfo.tcgPlayerImageUrl || parseCardImage(v) || v.imageUrl || v.image_url || v.image || activeImage;
                          const isSelected = selectedVariant === v;

                          return (
                            <div
                              key={idx}
                              onClick={() => setSelectedVariant(isSelected ? null : v)}
                              className={`group relative p-2.5 rounded-xl border text-left cursor-pointer transition-all flex items-center justify-between gap-3 ${
                                isSelected 
                                  ? 'bg-purple-950/50 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.25)] ring-1 ring-purple-500/50' 
                                  : 'bg-slate-950/40 border-white/5 hover:border-white/20 hover:bg-slate-900/60'
                              }`}
                            >
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                {vImg ? (
                                  <div className="w-10 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-950 border border-white/10 shadow-sm flex items-center justify-center group-hover:border-white/20 transition-colors">
                                    <CardImage src={vImg} alt={vInfo.primaryLabel} objectFit="contain" className="w-full h-full" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-14 shrink-0 rounded-lg bg-slate-900 border border-white/5 flex items-center justify-center text-slate-600 text-[10px]">
                                    <i className="fas fa-image"></i>
                                  </div>
                                )}
                                
                                <div className="min-w-0 flex-1 space-y-1">
                                  <div className="flex items-center space-x-1.5 min-w-0">
                                    {vInfo.setCode && (
                                      <span className="px-1.5 py-0.5 bg-blue-950/80 text-blue-300 font-mono font-bold text-[10px] uppercase rounded border border-blue-500/30 shrink-0">
                                        {vInfo.setCode}
                                      </span>
                                    )}
                                    <span 
                                      className={`text-xs font-semibold truncate ${isSelected ? 'text-purple-200' : 'text-slate-200'}`}
                                      title={vInfo.setName || vInfo.primaryLabel}
                                    >
                                      {vInfo.setName || vInfo.primaryLabel}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center space-x-1.5 text-[10px] text-slate-400">
                                    {vInfo.rarity && (
                                      <span className="font-medium text-orange-300/90">
                                        {vInfo.rarity}
                                      </span>
                                    )}
                                    {vInfo.rarity && vInfo.printing && vInfo.printing !== vInfo.rarity && (
                                      <span className="text-slate-600">•</span>
                                    )}
                                    {vInfo.printing && vInfo.printing !== vInfo.rarity && (
                                      <span className="font-medium text-purple-300/90">
                                        {vInfo.printing}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="text-right shrink-0 flex flex-col items-end justify-center pl-1">
                                <span className={`text-xs font-bold ${vInfo.priceNum > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                                  {vInfo.priceNum > 0 ? `R$ ${vInfo.priceNum.toFixed(2)}` : 'S/ Preço'}
                                </span>
                                {isSelected ? (
                                  <span className="inline-flex items-center space-x-1 text-[9px] font-black uppercase text-purple-400 tracking-wider mt-0.5">
                                    <i className="fas fa-check text-[8px]"></i>
                                    <span>Ativa</span>
                                  </span>
                                ) : (
                                  <span className="text-[9px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 font-medium">
                                    Selecionar
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer Nav */}
            <div className="px-6 py-3 border-t border-white/5 bg-slate-950/80 flex items-center justify-between">
              {cardsList.length > 0 && currentCardIndex !== -1 && onSelectCard ? (
                <div className="flex items-center justify-between w-full">
                  <button
                    disabled={currentCardIndex <= 0}
                    onClick={() => onSelectCard(getTargetCard(cardsList[currentCardIndex - 1]))}
                    className="px-3 py-1 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-white transition-all flex items-center space-x-1"
                  >
                    <i className="fas fa-chevron-left text-[10px]"></i>
                    <span>Anterior</span>
                  </button>
                  <div className="bg-slate-800/50 px-3 py-1 rounded-full border border-white/5 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    {currentCardIndex + 1} de {cardsList.length}
                  </div>
                  <button
                    disabled={currentCardIndex >= cardsList.length - 1}
                    onClick={() => onSelectCard(getTargetCard(cardsList[currentCardIndex + 1]))}
                    className="px-3 py-1 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-white transition-all flex items-center space-x-1"
                  >
                    <span>Próxima</span>
                    <i className="fas fa-chevron-right text-[10px]"></i>
                  </button>
                </div>
              ) : (
                <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                  Detalhes da Carta
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Fullscreen Card Image Modal */}
      <AnimatePresence>
        {fullscreenImageCard && (
          <div
            onClick={() => setFullscreenImageCard(null)}
            className="fixed inset-0 z-[110] flex flex-col items-center justify-center p-4 bg-black/90 cursor-zoom-out"
          >
            <div className="relative max-w-lg w-full aspect-[63/88] rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 flex items-center justify-center">
              <CardImage 
                src={parseCardImage(fullscreenImageCard) || fullscreenImageCard.imageUrl || fullscreenImageCard.image_url || fullscreenImageCard.image} 
                alt={fullscreenImageCard.name}
                game={fullscreenImageCard.game || gameName}
                gameId={fullscreenImageCard.game_id}
                cardBackUrl={fullscreenImageCard.cardBackUrl || fullscreenImageCard.card_back_url}
                objectFit="contain"
                className="w-full h-full" 
              />
            </div>
            <p className="mt-4 text-xs font-black uppercase tracking-widest text-slate-400 flex items-center space-x-2">
              <i className="fas fa-times"></i>
              <span>Clique em qualquer lugar para fechar</span>
            </p>
          </div>
        )}
      </AnimatePresence>

      {/* Card Bug Report Modal */}
      <CardBugReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        card={card}
        cardCode={cardCode}
        gameName={card.game || gameName}
        cardSet={selectedVariantInfo?.setName || cardSet}
        cardRarity={selectedVariantInfo?.rarity || cardRarityDisplay}
        activeImage={activeImage || defaultCardImage}
        selectedVariantInfo={selectedVariantInfo}
      />
    </>
  );
};
